import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import type { SchoolClassOptionTypes } from 'teacher-ui-client/components/base/select/school-class/options'
import { AllClassesOption, MyClassesOption } from 'teacher-ui-client/components/base/select/school-class/options'
import type SessionService from 'teacher-ui-client/services/session'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type Student from 'teacher-ui-client/models/student'
import type Assignment from 'teacher-ui-client/models/assignment'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { optional } from 'teacher-ui-client/helpers/optional'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { eq } from 'ember-truth-helpers'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import { StudentsTableRow } from 'teacher-ui-client/components/assignments-admin/students-table/row'
import { SchoolClassSelect } from 'teacher-ui-client/components/base/select/school-class'
import { InnerAssignmentsTable } from 'teacher-ui-client/components/assignments-admin/students-table/inner-assignments-table'
import { colspanMax } from 'teacher-ui-client/modifiers/colspan-max'
import { hasId } from 'teacher-ui-client/utils/type-guards'
import DataTableOperations from '@blakeelearning/data-tables/components/data-table-operations'
import { dtoComputed } from '@blakeelearning/data-tables/components/data-table-operations'
import { isEmpty } from '@ember/utils'
import type { PhosphorIconName } from 'phosphor-icons'
import { statefulSort, type TableAction } from 'teacher-ui-client/utils/giraffe/stateful-sort'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'

interface Signature {
  Args: {
    selectedStudent?: Student
    schoolClasses: SchoolClass[]
    students: Student[]
    assignments: Assignment[]
    subscriptionType: SubscriptionType
    goToStudentAction?: (id: string) => unknown
    selectedSchoolClassId: SchoolClassOptionTypes | string
    selectSchoolClassIdAction?: (id: SchoolClassOptionTypes | string) => unknown
  }
}

export interface DataRow {
  id: string
  firstName: string
  lastName: string
  gradePosition: number
  schoolClassNames: string
  incompleteCount: number
}

type DTOComponentContext = any

/**
 * This component renders a sortable list of students and their assignments
 */
export class StudentsTable extends Component<Signature> {
  @service session!: SessionService

  @tracked sortPriorityQueue = ['firstName', 'lastName', 'gradePosition'].map((key) => ({
    key,
    sortDirection: 'asc' as const,
  }))

  @tracked currentSortIcon: PhosphorIconName = 'phosphor-caret-up-down-fill'
  @tracked currentSortColumn?: string

  @tracked currentPage = 1
  @tracked perPage = 20

  get pageCount() {
    const dataLength = this.args.students.length ?? 0
    const { perPage } = this
    return Math.ceil(dataLength / perPage)
  }
  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.args.subscriptionType)
  }

  /**
   * if has any classes given that are not their own, show "all classes" option. always provide "my classes" option.
   */
  get schoolClassAdditionalOptions() {
    const otherSchoolClassesGiven = this.args.schoolClasses.some(
      (sc) => !sc.teachers.includes(this.session.currentTeacher),
    )
    return otherSchoolClassesGiven ? [AllClassesOption, MyClassesOption] : [MyClassesOption]
  }

  get data(): DataRow[] {
    return this.args.students.filter(hasId).map((student) => {
      const { id, firstName, lastName, gradePosition, schoolClasses } = student
      const schoolClassNames = schoolClasses
        .filter((sc) => sc.subscriptionType === this.args.subscriptionType)
        .map((sc) => sc.name)
        .join(', ')
      const assignmentsForStudent = this.args.assignments.filter((assignment) =>
        assignment.assignedStudents?.some((assignedStudent) => String(assignedStudent.canonical_student_id) === id),
      )
      // count each assignment where this student hasn't completed it
      const incompleteCount = assignmentsForStudent.filter((assignment) =>
        assignment.assignedStudents?.some((as) => String(as.canonical_student_id) === id && as.status !== 'completed'),
      ).length
      return { id, firstName, lastName, gradePosition, schoolClassNames, incompleteCount }
    })
  }

  get assignmentsForSelectedStudent() {
    return this.args.assignments.filter((assignment) =>
      assignment.assignedStudents?.some(
        (assignedStudent) => String(assignedStudent.canonical_student_id) === this.args.selectedStudent?.id,
      ),
    )
  }

  get headerClass() {
    switch (this.args.subscriptionType) {
      case SubscriptionType.Reading:
        return 'bg-oceany-blue-100'
      case SubscriptionType.Maths:
        return 'bg-ms-green-100'
      case SubscriptionType.Writing:
        return 'bg-wl-blue-100'
      default:
        return ''
    }
  }

  get dataOperations() {
    return {
      doPagination: dtoComputed(
        'data.[]',
        'currentPage',
        'perPage',
        function (this: DTOComponentContext, data: DataRow[]) {
          const { currentPage, perPage } = this
          if (isEmpty(currentPage) || isEmpty(perPage)) return data
          const upperIndex = currentPage * perPage
          const lowerIndex = (currentPage - 1) * perPage
          return data.slice(lowerIndex, upperIndex)
        },
      ),
    }
  }

  operationsStack(this: DTOComponentContext) {
    return [...this.defaultOperationsStack, 'doPagination']
  }

  changePage = (page: number) => {
    this.currentPage = page
  }

  changePerPage = (perPage: number) => {
    this.perPage = perPage
    this.currentPage = 1 // reset to first page when perPage changes
  }

  statefulSort = (tableAction: TableAction, tableActionOperation: string, tableActionKey: string) => {
    return statefulSort(this, tableAction, tableActionOperation, tableActionKey)
  }

  <template>
    {{#if this.isSubco}}
      <div class="flex flex-nowrap items-center gap-3 px-3 py-4 {{this.headerClass}}">
        <span class="font-bold">{{t "assignments.headings.dropDownLabel"}}:</span>
        <SchoolClassSelect
          data-test-assignments-class-select
          @additionalOptions={{this.schoolClassAdditionalOptions}}
          @schoolClasses={{@schoolClasses}}
          @schoolClassId={{@selectedSchoolClassId}}
          @updateSchoolClassId={{optional @selectSchoolClassIdAction}}
          @themeKey={{themeKey @subscriptionType}}
        />
      </div>
    {{/if}}
    <DataTableOperations
      @dataOperations={{this.dataOperations}}
      @operationsStack={{this.operationsStack}}
      @currentPage={{this.currentPage}}
      @perPage={{this.perPage}}
      @data={{this.data}}
      as |transformedData tableAction|
    >
      <table data-test-students-table class="w-full divide-y divide-white overflow-auto border-t border-white">

        <thead>
          <tr class="{{this.headerClass}} divide-x divide-white">
            <th class="w-0 border-r-0 px-3 py-4"></th>
            <th
              data-heading="First Name"
              class="px-3 py-4"
              {{on "click" (fn this.statefulSort tableAction "sortByValue" "firstName")}}
              role="button"
            >
              <div class="flex flex-nowrap items-center gap-4">
                <span>{{t "assignments.firstName"}}</span>
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "firstName")
                    this.currentSortIcon
                    "phosphor-caret-up-down-fill"
                  }}
                  class="shrink-0"
                />
              </div>
            </th>
            <th
              data-heading="Last Name"
              class="px-3 py-4"
              {{on "click" (fn this.statefulSort tableAction "sortByValue" "lastName")}}
              role="button"
            >
              <div class="flex flex-nowrap items-center gap-4">
                <span>{{t "assignments.lastName"}}</span>
                <PhosphorIcon
                  @icon={{if (eq this.currentSortColumn "lastName") this.currentSortIcon "phosphor-caret-up-down-fill"}}
                  class="shrink-0"
                />
              </div>
            </th>
            <th
              data-heading="Classes"
              class="px-3 py-4"
              {{on "click" (fn this.statefulSort tableAction "sortByValue" "schoolClassNames")}}
              role="button"
            >
              <div class="flex flex-nowrap items-center gap-4">
                <span>{{t "assignments.classes"}}</span>
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "schoolClassNames")
                    this.currentSortIcon
                    "phosphor-caret-up-down-fill"
                  }}
                  class="shrink-0"
                />
              </div>
            </th>
            <th
              data-heading="Grade"
              class="px-3 py-4"
              {{on "click" (fn this.statefulSort tableAction "sortByValue" "gradePosition")}}
              role="button"
            >
              <div class="flex flex-nowrap items-center gap-4">
                <span>{{t "grade.label"}}</span>
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "gradePosition")
                    this.currentSortIcon
                    "phosphor-caret-up-down-fill"
                  }}
                  class="shrink-0"
                />
              </div>
            </th>
            <th
              data-heading="Incomplete"
              class="px-3 py-4"
              {{on "click" (fn this.statefulSort tableAction "sortByValue" "incompleteCount")}}
              role="button"
            >
              <div class="flex flex-nowrap items-center gap-4">
                <span>{{t "assignments.headings.numberIncomplete"}}</span>
                <PhosphorIcon
                  @icon={{if
                    (eq this.currentSortColumn "incompleteCount")
                    this.currentSortIcon
                    "phosphor-caret-up-down-fill"
                  }}
                  class="shrink-0"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="divide-dusty-black-100 divide-y">
          {{#each transformedData as |row|}}
            <StudentsTableRow
              @row={{row}}
              @goToStudentAction={{optional @goToStudentAction}}
              @selectedStudentId={{@selectedStudent.id}}
              @subscriptionType={{@subscriptionType}}
            />
            {{#if @selectedStudent}}
              {{#if (eq row.id @selectedStudent.id)}}
                <tr>
                  <td {{colspanMax}} class="p-0">
                    <InnerAssignmentsTable
                      class="border-t border-white"
                      @student={{@selectedStudent}}
                      @assignmentsForSelectedStudent={{this.assignmentsForSelectedStudent}}
                    />
                  </td>
                </tr>
              {{/if}}
            {{/if}}
          {{else}}
            <tr>
              <td {{colspanMax}} class="p-3">
                {{t "assignments.missingStudentsDataInList"}}
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>
    </DataTableOperations>
    <BasePageSelector
      class="my-4"
      @pageCount={{this.pageCount}}
      @currentPage={{this.currentPage}}
      @changePage={{this.changePage}}
      @itemCount={{@students.length}}
      @itemsPerPage={{this.perPage}}
      @changePerPage={{this.changePerPage}}
    />
  </template>
}

export default StudentsTable
